import type {IssuePickerItem} from '@github-ui/item-picker/IssuePicker'
import {useIssueFiltering} from '@github-ui/item-picker/useIssueFiltering'
import {ActionList, Spinner, TextInput} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {Dialog} from '@primer/react/experimental'
import {useCallback, useMemo, useRef, useState} from 'react'
import {getIssueIconAndFill} from '../utils/issue-icon'

export type IssueDialogPickerProps = {
  repositoryId: string
  repository: string
  owner: string
  onClose: () => void
  selectedIssue: IssuePickerItem | null
  onIssueSelected: (issue: IssuePickerItem | null) => void
  hiddenIssueIds?: string[]
}

export function IssueDialogPicker({
  repositoryId,
  repository,
  owner,
  onClose,
  selectedIssue: initialSelectedIssue,
  onIssueSelected,
  hiddenIssueIds,
}: IssueDialogPickerProps): JSX.Element | undefined {
  const returnFocusRef = useRef(null)
  const [filter, setFilter] = useState('')
  const [selectedIssue, setSelectedIssue] = useState<IssuePickerItem | null>(initialSelectedIssue)
  const {isLoading, isError, items} = useIssueFiltering(filter, owner, `${owner}/${repository}`, hiddenIssueIds)

  const onItemSelected = useCallback((issue: IssuePickerItem | null) => {
    setSelectedIssue(issue)
  }, [])

  const onCancel = useCallback(() => {
    setSelectedIssue(null)
    onIssueSelected(null)
    onClose()
  }, [onClose, onIssueSelected])

  const onApply = useCallback(() => {
    onIssueSelected(selectedIssue)
  }, [onIssueSelected, selectedIssue])

  return (
    <Dialog
      returnFocusRef={returnFocusRef}
      title={`${owner}/${repository}`}
      subtitle={'Select which issue this is duplicate of'}
      onClose={onClose}
      aria-labelledby="header"
      footerButtons={[
        {buttonType: 'default', content: 'Cancel', onClick: onCancel},
        {buttonType: 'primary', content: 'Apply', onClick: onApply},
      ]}
    >
      <div>
        <TextInput placeholder="Filter or paste URL" onChange={e => setFilter(e.target.value)} sx={{width: '100%'}} />
      </div>
      <IssueDialogPickerInternal
        isLoading={isLoading}
        isError={isError}
        items={items}
        onIssueSelected={onItemSelected}
        selectedIssue={selectedIssue}
        repositoryId={repositoryId}
      />
    </Dialog>
  )
}

type IssueDialogPickerInternalProps = {
  isLoading: boolean
  isError: boolean
  items: Map<string, IssuePickerItem>
  selectedIssue: IssuePickerItem | null
} & Pick<IssueDialogPickerProps, 'repositoryId' | 'onIssueSelected'>

function IssueDialogPickerInternal({
  isLoading,
  isError,
  items,
  selectedIssue,
  onIssueSelected,
  repositoryId,
}: IssueDialogPickerInternalProps): JSX.Element | undefined {
  const onIssueSelection = useCallback(
    (selectedIssues: IssuePickerItem[]) => {
      if (selectedIssues && selectedIssues.length > 0 && selectedIssues[0]) {
        onIssueSelected(selectedIssues[0])
      }
    },
    [onIssueSelected],
  )

  // check if the selected issue is in the list of items
  // that could not be the case if the issue was selected by pasting a url from another repo
  const renderedItems = useMemo(() => {
    const isSelectedIssueInItems = selectedIssue && items.has(selectedIssue.id)
    if (selectedIssue && !isSelectedIssueInItems) {
      const map = new Map<string, IssuePickerItem>()
      map.set(selectedIssue.id, selectedIssue)
      return map
    } else {
      return items
    }
  }, [items, selectedIssue])

  if (isError) {
    return <>Error loading issues</>
  }
  if (isLoading) {
    return (
      <div style={{margin: 16, display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
        <Spinner size="medium" />
        Loading issues
      </div>
    )
  }
  if (items.size === 0) {
    return <>No issues found</>
  }

  return (
    <ActionList selectionVariant="single">
      {[...renderedItems.values()].map(issue => {
        const {icon, fill} = getIssueIconAndFill(issue.state, issue.stateReason)
        return (
          <ActionList.Item
            key={issue.id}
            onSelect={() => onIssueSelection([issue])}
            selected={selectedIssue?.id === issue.id}
          >
            <ActionList.LeadingVisual>
              <Octicon icon={icon} sx={{path: {fill}}} />
            </ActionList.LeadingVisual>
            {issue.title}
            <ActionList.Description variant="block">{getIssueDescription(repositoryId, issue)}</ActionList.Description>
          </ActionList.Item>
        )
      })}
    </ActionList>
  )
}

function getIssueDescription(repositoryId: string, issue: IssuePickerItem): string {
  let description = ''
  if (issue.repository.id !== repositoryId) {
    description = `${issue.repository.nameWithOwner}`
  }
  return `${description}#${issue.number}`
}

try{ IssueDialogPicker.displayName ||= 'IssueDialogPicker' } catch {}
try{ IssueDialogPickerInternal.displayName ||= 'IssueDialogPickerInternal' } catch {}